import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";
import { CreateAttribute$Params } from "../../../services/generated/waba-api/fn/referential-attribute-api/create-attribute";
import { GetAttributeByRef$Params } from "../../../services/generated/waba-api/fn/referential-attribute-api/get-attribute-by-ref";
import { PatchArchiveStatus2$Params } from "../../../services/generated/waba-api/fn/referential-attribute-api/patch-archive-status-2";
import { UpdateAttribute$Params } from "../../../services/generated/waba-api/fn/referential-attribute-api/update-attribute";
import { Attribute } from "../../../services/generated/waba-api/models";
import { ReferentialAttributeApiService } from "../../../services/generated/waba-api/services";
import { AlertService } from "../../../shared/alert/service/alert.service";
import { AttributeCatalogService } from "../../../services/attribute/attribute-catalog.service";

@Component({
  selector: "app-attribute-create-panel",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, TranslateModule],
  templateUrl: "./attribute-create-panel.component.html",
  styleUrl: "./attribute-create-panel.component.scss",
})
export class AttributeCreatePanelComponent implements OnInit, OnChanges {
  @Input() open = false;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  isRTL = false;
  currentView: "list" | "form" = "list";

  // ── List state ──
  attributes: Attribute[] = [];
  filteredAttributes: Attribute[] = [];
  paginatedAttributes: Attribute[] = [];
  isLoading = false;
  searchTerm = "";
  totalElements = 0;
  currentPage = 0;
  pageSize = 10;

  // ── Form state ──
  attributeForm!: FormGroup;
  isEditMode = false;
  editingReference: string | null = null;
  isSaving = false;
  isListAttribute = false;

  attributeTypes = [
    "NUMERIC",
    "TAG",
    "ALPHABETIC",
    "ALPHANUMERIC",
    "LIST",
    "MULTI_SELECT",
    "OTHER",
  ];

  constructor(
    private fb: FormBuilder,
    private attributeService: ReferentialAttributeApiService,
    private alertService: AlertService,
    private translateService: TranslateService,
    private attributeCatalog: AttributeCatalogService,
  ) {}

  ngOnInit(): void {
    this.isRTL = document.documentElement.getAttribute("dir") === "rtl";
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["open"] && this.open) {
      this.currentView = "list";
      this.searchTerm = "";
      this.currentPage = 0;
      this.loadAttributes();
    }
  }

  // ── Close ──
  onClose(): void {
    this.currentView = "list";
    this.closed.emit();
  }

  // ══════════════════════════════════════════
  //  LIST VIEW
  // ══════════════════════════════════════════

  loadAttributes(): void {
    this.isLoading = true;
    this.attributeService
      .searchAttribute({
        body: {
          page: 0,
          size: 500,
          filter: { name: "", archived: null, attributeType: null } as any,
        },
        "x-api-canal": "web",
        "x-api-requestId": uuidv4(),
      })
      .subscribe({
        next: (response) => {
          this.attributes = response?.body?.entities ?? [];
          this.applyFilter();
          this.isLoading = false;
        },
        error: () => {
          this.attributes = [];
          this.filteredAttributes = [];
          this.paginatedAttributes = [];
          this.totalElements = 0;
          this.isLoading = false;
        },
      });
  }

  onSearchChange(): void {
    this.currentPage = 0;
    this.applyFilter();
  }

  private applyFilter(): void {
    const term = this.searchTerm.toLowerCase().trim();
    this.filteredAttributes = term
      ? this.attributes.filter((a) =>
          (a.name ?? "").toLowerCase().includes(term),
        )
      : [...this.attributes];
    this.totalElements = this.filteredAttributes.length;
    this.applyPagination();
  }

  private applyPagination(): void {
    const start = this.currentPage * this.pageSize;
    this.paginatedAttributes = this.filteredAttributes.slice(
      start,
      start + this.pageSize,
    );
  }

  // ── Pagination helpers ──
  get rangeStart(): number {
    return this.totalElements === 0 ? 0 : this.currentPage * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(
      (this.currentPage + 1) * this.pageSize,
      this.totalElements,
    );
  }

  get canGoPrevious(): boolean {
    return this.currentPage > 0;
  }

  get canGoNext(): boolean {
    return (this.currentPage + 1) * this.pageSize < this.totalElements;
  }

  goPrevious(): void {
    if (this.canGoPrevious) {
      this.currentPage--;
      this.applyPagination();
    }
  }

  goNext(): void {
    if (this.canGoNext) {
      this.currentPage++;
      this.applyPagination();
    }
  }

  // ── Archive toggle ──
  toggleArchive(attr: Attribute): void {
    const params: PatchArchiveStatus2$Params = {
      "x-api-canal": "web",
      "x-api-requestId": uuidv4(),
      ref: attr.reference!,
      archive: !attr.archived,
    };
    this.attributeService.patchArchiveStatus2(params).subscribe({
      next: () => {
        this.attributeCatalog.invalidate();
        this.alertService.showSuccess(
          this.translateService.instant("APP_SWAL.SUCCESS_OPERATION"),
        );
        this.loadAttributes();
      },
      error: () => {
        this.alertService.showError(
          this.translateService.instant("APP_SWAL.ERROR"),
        );
      },
    });
  }

  // ── Get attribute name ──
  getAttributeName(attr: Attribute): string {
    return attr.name ?? "";
  }

  // ── Get list values preview ──
  getValuesPreview(attr: Attribute): string {
    if (
      (attr.attributeType !== "LIST" && attr.attributeType !== "MULTI_SELECT") ||
      !attr.attributeListValues ||
      attr.attributeListValues.length === 0
    ) {
      return "";
    }
    const preview = attr.attributeListValues.slice(0, 5).join(", ");
    return attr.attributeListValues.length > 5 ? preview + "..." : preview;
  }

  // ══════════════════════════════════════════
  //  FORM VIEW
  // ══════════════════════════════════════════

  private initForm(): void {
    this.attributeForm = this.fb.group({
      name: [null, Validators.required],
      attributeType: [null, Validators.required],
      attributeListValues: this.fb.array([]),
    });
    this.isListAttribute = false;
  }

  openCreateForm(): void {
    this.initForm();
    this.isEditMode = false;
    this.editingReference = null;
    this.currentView = "form";
  }

  openEditForm(attr: Attribute): void {
    this.initForm();
    this.isEditMode = true;
    this.editingReference = attr.reference ?? null;
    this.currentView = "form";
    if (attr.reference) {
      this.loadAttributeForEdit(attr.reference);
    }
  }

  backToList(): void {
    this.currentView = "list";
  }

  private loadAttributeForEdit(ref: string): void {
    this.isLoading = true;
    const params: GetAttributeByRef$Params = {
      ref,
      "x-api-canal": "web",
      "x-api-requestId": uuidv4(),
    };
    this.attributeService.getAttributeByRef(params).subscribe({
      next: (response) => {
        if (response?.body) {
          this.attributeForm.patchValue(response.body);
          if (
            this.isOptionsAttributeType(response.body.attributeType) &&
            response.body.attributeListValues
          ) {
            this.isListAttribute = true;
            response.body.attributeListValues.forEach((value) => {
              this.attributeListValuesArray.push(
                this.fb.group({ value: [value, Validators.required] }),
              );
            });
          }
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.alertService.showError(
          this.translateService.instant("APP_GENERIC_ALERTS.LOAD_FAILED"),
        );
        this.backToList();
      },
    });
  }

  // ── Form Array helpers ──
  get attributeListValuesArray(): FormArray {
    return this.attributeForm.get("attributeListValues") as FormArray;
  }

  attributeTypeChange(): void {
    this.isListAttribute = this.isOptionsAttributeType(
      this.attributeForm.get("attributeType")?.value,
    );
    if (this.isListAttribute && this.attributeListValuesArray.length === 0) {
      this.addListValue();
    } else if (!this.isListAttribute) {
      this.attributeListValuesArray.clear();
    }
  }

  private isOptionsAttributeType(type: string | null | undefined): boolean {
    return type === "LIST" || type === "MULTI_SELECT";
  }

  addListValue(): void {
    this.attributeListValuesArray.push(
      this.fb.group({ value: [null, Validators.required] }),
    );
  }

  removeListValue(index: number): void {
    if (this.attributeListValuesArray.length > 1) {
      this.attributeListValuesArray.removeAt(index);
    }
  }

  private validateUniqueValues(): boolean {
    const values = this.attributeListValuesArray.controls
      .map((c) => (c.get("value")?.value ?? "").trim())
      .filter((v) => !!v);
    const unique = new Set(values);
    if (unique.size !== values.length) {
      this.alertService.showError(
        this.translateService.instant("ATTRIBUTE.FORM.UNIQUE_VALUE"),
      );
      return false;
    }
    return true;
  }

  // ── Submit ──
  submitForm(): void {
    if (this.attributeForm.invalid) {
      this.attributeForm.markAllAsTouched();
      this.attributeListValuesArray.markAllAsTouched();
      const invalidFields = this.getInvalidFieldLabels();
      this.alertService.showError(
        this.translateService.instant("ATTRIBUTE.FORM.INVALID_FIELDS", {
          fields: invalidFields.join(", "),
        }),
      );
      this.scrollFirstInvalidIntoView();
      return;
    }

    if (this.isListAttribute && !this.validateUniqueValues()) {
      return;
    }

    const body: Attribute = {
      ...this.attributeForm.value,
      attributeListValues: this.attributeListValuesArray.value
        .map((item: { value: string }) => (item?.value ?? "").trim())
        .filter((value: string) => !!value),
    };

    this.isSaving = true;

    if (this.isEditMode && this.editingReference) {
      const params: UpdateAttribute$Params = {
        "x-api-canal": "web",
        "x-api-requestId": uuidv4(),
        body,
        ref: this.editingReference,
      };
      this.attributeService.updateAttribute(params).subscribe({
        next: (res) => {
          this.isSaving = false;
          // The write reached the server; drop the cache whatever status the
          // envelope carries, so an unexpected code cannot strand a stale list.
          this.attributeCatalog.invalidate();
          if (res?.status === "200" && res.body) {
            this.alertService.showSuccess(
              this.translateService.instant("ATTRIBUTE.MODAL.UPDATE_SUCCESS"),
            );
            this.saved.emit();
            this.loadAttributes();
            this.backToList();
          } else {
            this.alertService.showError(
              res?.message ??
                this.translateService.instant(
                  "APP_GENERIC_ALERTS.GENERIC_ERROR",
                ),
            );
          }
        },
        error: () => {
          this.isSaving = false;
          this.alertService.showError(
            this.translateService.instant("APP_GENERIC_ALERTS.UPDATE_FAILED"),
          );
        },
      });
    } else {
      const params: CreateAttribute$Params = {
        "x-api-canal": "web",
        "x-api-requestId": uuidv4(),
        body,
      };
      this.attributeService.createAttribute(params).subscribe({
        next: (res) => {
          this.isSaving = false;
          this.attributeCatalog.invalidate();
          if (res?.status === "200" && res.body) {
            this.alertService.showSuccess(
              this.translateService.instant("ATTRIBUTE.MODAL.SAVE_SUCCESS"),
            );
            this.saved.emit();
            this.loadAttributes();
            this.backToList();
          } else {
            this.alertService.showError(
              res?.message ??
                this.translateService.instant(
                  "APP_GENERIC_ALERTS.GENERIC_ERROR",
                ),
            );
          }
        },
        error: () => {
          this.isSaving = false;
          this.alertService.showError(
            this.translateService.instant(
              "APP_GENERIC_ALERTS.CREATION_FAILED",
            ),
          );
        },
      });
    }
  }

  private scrollFirstInvalidIntoView(): void {
    setTimeout(() => {
      const invalid = document.querySelector<HTMLElement>(
        ".acp-panel .acp__input--invalid",
      );
      invalid?.scrollIntoView({ behavior: "smooth", block: "center" });
      invalid?.focus?.();
    });
  }

  private getInvalidFieldLabels(): string[] {
    const labels: string[] = [];
    
    if (this.attributeForm.get("name")?.invalid) {
      labels.push(this.translateService.instant("ATTRIBUTE.FORM.NAME"));
    }
    if (this.attributeForm.get("attributeType")?.invalid) {
      labels.push(this.translateService.instant("ATTRIBUTE.FORM.TYPE"));
    }
    
    if (this.isListAttribute) {
      const invalidValues = this.attributeListValuesArray.controls
        .filter((c) => c.get("value")?.invalid)
        .length;
      if (invalidValues > 0) {
        labels.push(
          this.translateService.instant("ATTRIBUTE.MODAL.VALUES_LABEL") +
            ` (${invalidValues})`,
        );
      }
    }
    
    return labels.length
      ? labels
      : [this.translateService.instant("APP_GENERIC_ALERTS.REQUIRED_FIELDS")];
  }
}
